import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  DotsThreeVertical,
  PaperPlaneTilt,
  Star,
  LinkBreak,
  MicrosoftOutlookLogo,
  Info,
  Copy,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, formatApiErrorDetail } from "@/lib/api";

const Badge = ({ children, tone = "zinc" }) => {
  const tones = {
    zinc: "border-zinc-700 bg-zinc-800 text-zinc-200",
    green: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    amber: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  };
  return (
    <span
      className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

export const EmailingTab = () => {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: payload } = await api.get("/my-mail");
      setData(payload);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not load your mailboxes",
      );
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const connected = params.get("connected");
    const error = params.get("error");
    if (connected) toast.success(`${connected} connected`);
    if (error) toast.error(error);
    if (connected || error) {
      params.delete("connected");
      params.delete("error");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/my-mail/connect");
      window.location.assign(res.url);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not start the Microsoft sign-in",
      );
      setBusy(false);
    }
  };

  const act = async (fn, ok) => {
    try {
      const { data: payload } = await fn();
      setData(payload?.connections ? payload : null);
      if (!payload?.connections) await load();
      toast.success(ok);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "That did not work",
      );
    }
  };

  const test = async (id) => {
    try {
      const { data: res } = await api.post(`/my-mail/${id}/test`);
      toast.success(res.message);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Microsoft would not send that test",
      );
      load();
    }
  };

  const rows = data?.connections || [];

  return (
    <div className="space-y-4" data-testid="emailing-tab">
      <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
          Emails
        </div>

        <div className="mt-4 overflow-hidden rounded-lg border border-zinc-800">
          <div className="hidden grid-cols-[1.6fr_0.8fr_0.8fr_auto] gap-4 border-b border-zinc-800 bg-zinc-950 px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500 sm:grid">
            <span>Email</span>
            <span>Provider</span>
            <span>Status</span>
            <span className="w-8" />
          </div>

          {rows.length === 0 && (
            <p
              className="px-4 py-6 text-sm text-zinc-500"
              data-testid="no-mailboxes"
            >
              No mailbox connected yet. Add your Outlook account and the quotes,
              invoices and portal invites you send will go out from your own
              address.
            </p>
          )}

          {rows.map((row) => (
            <div
              key={row.id}
              data-testid={`mailbox-row-${row.email}`}
              className="grid grid-cols-[1fr_auto] items-center gap-3 border-b border-zinc-800/70 px-4 py-3 last:border-0 sm:grid-cols-[1.6fr_0.8fr_0.8fr_auto] sm:gap-4"
            >
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="truncate text-sm font-semibold">
                  {row.email}
                </span>
                {row.is_default && <Badge>Default</Badge>}
              </div>
              <div className="hidden sm:block">
                <Badge>Outlook</Badge>
              </div>
              <div className="hidden sm:block">
                <Badge tone={row.status === "Logged in" ? "green" : "amber"}>
                  {row.status}
                </Badge>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    data-testid={`mailbox-menu-${row.email}`}
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0 text-zinc-400 hover:bg-zinc-800"
                  >
                    <DotsThreeVertical size={20} weight="bold" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  data-testid={`mailbox-menu-content-${row.email}`}
                  className="w-56 border-zinc-800 bg-zinc-900"
                >
                  <DropdownMenuItem
                    data-testid="mailbox-test"
                    onClick={() => test(row.id)}
                    className="text-xs font-semibold uppercase tracking-widest"
                  >
                    <PaperPlaneTilt size={15} weight="bold" className="mr-2" />
                    Send test email
                  </DropdownMenuItem>
                  {!row.is_default && (
                    <DropdownMenuItem
                      data-testid="mailbox-default"
                      onClick={() =>
                        act(
                          () => api.post(`/my-mail/${row.id}/default`),
                          `${row.email} is now your default`,
                        )
                      }
                      className="text-xs font-semibold uppercase tracking-widest"
                    >
                      <Star size={15} weight="bold" className="mr-2" />
                      Make default
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    data-testid="mailbox-disconnect"
                    onClick={() =>
                      act(
                        () => api.delete(`/my-mail/${row.id}`),
                        `${row.email} disconnected`,
                      )
                    }
                    className="text-xs font-semibold uppercase tracking-widest text-red-400"
                  >
                    <LinkBreak size={15} weight="bold" className="mr-2" />
                    Disconnect
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {row.last_error && (
                <p className="col-span-2 text-xs text-red-400 sm:col-span-4">
                  {row.last_error}
                </p>
              )}
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-2 border-t border-zinc-800 bg-zinc-950 px-4 py-3">
            <Button
              data-testid="add-outlook-button"
              variant="ghost"
              disabled={busy}
              onClick={connect}
              className="h-11 text-xs font-bold uppercase tracking-widest text-indigo-300 hover:bg-indigo-500/10"
            >
              <Plus size={16} weight="bold" className="mr-2" />
              {busy ? "Opening Microsoft…" : "Add Outlook Email"}
            </Button>
            <span className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-600">
              <MicrosoftOutlookLogo size={15} weight="bold" />
              Microsoft 365
            </span>
          </div>
        </div>

        <p className="mt-4 text-xs text-zinc-500">
          {data?.shop_sender
            ? `Anything sent automatically still goes out from the shop mailbox (${data.shop_sender}).`
            : "Anything sent automatically still goes out from the shop's built-in sender."}
        </p>
      </div>

      {data && !data.app_configured && (
        <div
          className="rounded-lg border border-amber-500/25 bg-amber-500/[0.06] p-4 text-sm text-amber-200"
          data-testid="mail-not-configured"
        >
          Ask an admin to fill in the Microsoft tenant ID, client ID and secret
          under Settings → Microsoft 365 Email first. The same app registration
          is reused here.
        </div>
      )}

      {data?.app_configured && (
        <div
          className="rounded-lg border border-indigo-400/25 bg-indigo-500/[0.06] p-4"
          data-testid="mail-setup-help"
        >
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-indigo-200">
            <Info size={15} weight="bold" /> One-time Microsoft setup
          </div>
          <p className="mt-2 text-sm text-zinc-300">
            If Microsoft says{" "}
            <span className="font-mono-data text-amber-300">
              AADSTS500113: No reply address is registered
            </span>
            , this step has not been done yet.
          </p>
          <ol className="mt-3 list-decimal space-y-3 pl-5 text-sm text-zinc-400">
            <li>
              Entra admin centre → App registrations → your app →{" "}
              <strong className="text-zinc-200">Authentication</strong> → Add a
              platform → <strong className="text-zinc-200">Web</strong>, then
              add both of these redirect URIs exactly:
              <div className="mt-2 space-y-2">
                {(data.redirect_uris || [data.redirect_uri]).map((uri, i) => (
                  <div key={uri} className="flex items-center gap-2">
                    <code
                      data-testid={
                        i === 0 ? "mail-redirect-uri" : `mail-redirect-uri-${i}`
                      }
                      className="min-w-0 flex-1 break-all rounded bg-zinc-950 px-2 py-1.5 text-[11px] text-indigo-300"
                    >
                      {uri}
                    </code>
                    <Button
                      data-testid={`copy-redirect-${i}`}
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard?.writeText(uri);
                        toast.success("Redirect URI copied");
                      }}
                      className="h-9 shrink-0 border-zinc-700 bg-zinc-950 px-3 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800"
                    >
                      <Copy size={14} weight="bold" className="mr-1.5" /> Copy
                    </Button>
                  </div>
                ))}
              </div>
              <span className="mt-1 block text-[11px] text-zinc-600">
                The first is this preview, the second is your live warehouse
                address — register both and it works in either place.
              </span>
            </li>
            <li>
              Same app →{" "}
              <strong className="text-zinc-200">API permissions</strong> → Add a
              permission → Microsoft Graph →{" "}
              <strong className="text-zinc-200">Delegated permissions</strong> →
              add <code className="text-indigo-300">Mail.Send</code>,{" "}
              <code className="text-indigo-300">User.Read</code> and{" "}
              <code className="text-indigo-300">offline_access</code>.
            </li>
            <li>
              Under Authentication, leave{" "}
              <strong className="text-zinc-200">
                Access tokens / ID tokens
              </strong>{" "}
              unticked — this uses the authorization code flow. Save, then press
              Add Outlook Email again.
            </li>
          </ol>
        </div>
      )}
    </div>
  );
};

export default EmailingTab;
